/**
 * A live session (D282) as the workbench draws its own conversation, shared by the Live page and a machine's Sessions
 * view: the person's messages as bubbles, the agent's words with the blocks drawn from them (RichMarkdown: an outcome
 * report's table, a test result, a steps tracker), its tool calls as one line per run that opens to the calls (HeroUI
 * Pro's ChatTool, the workbench's ToolCalls), the tasks it put on the board as the strip the desktop shows ("Tasks from
 * this chat · 2/5 done"), and at the foot what the desktop docks above its composer: the banners, an approval waiting
 * (the workbench's ApprovalCard: its kind, its risk in words, Approve, Allow for this session, Decline), then the
 * composer with its one Send or Stop.
 *
 * Since D372 (workbench 9a76e5e2) the stream says more, and every part reads it only when it is there, so an older
 * workbench draws as before: each message once as it is written (a Team Work member's by name, the workbench's own
 * lines, its dividers and a plan's Wrap-up card in place), an agent's question to a named person with its answer, a
 * turn's changed files under its reply, tool calls grouped by the member who made them and saying why one failed, an
 * approval's command or change, the usage limit's banner, and the task strip's people with Pause all / Resume all.
 *
 * The view parts are copied from the workbench (52bc328e, 9a76e5e2) with the desktop's messages swapped for the
 * relay's control calls (useLiveSession): ToolCalls (components/facilitator/TeamTurn.tsx), ApprovalCard
 * (components/agents/ApprovalCard.tsx, without the permission-mode popover: the console cannot change a conversation's
 * mode), TaskStrip (components/facilitator/TaskStrip.tsx, without Open board: the console's board is the project's),
 * the composer's primary action (ComposerPrimaryAction.tsx) and the message rows (ConversationTimeline.tsx). The turn's
 * own parts are LiveTurnParts.tsx. Their words and models are `@aioe/shared` (ui/liveSessionParts.ts,
 * ui/toolSummaryModel.ts, ui/derivedBlocks.ts, remote/liveTranscript.ts).
 */

import type { ApprovalAnswer, LiveApproval, LiveMessageItem, LiveToolItem, RemoteSessionMode, ReportedRuntime } from '@aioe/shared';
import {
  answersByQuestion,
  ANSWERS,
  approvalToolKind,
  authorWords,
  isLive,
  liveRows,
  liveToolActivity,
  MAX_TOOL_CALLS,
  personWaitLine,
  positionLabel,
  remoteHandOffChoices,
  REQUEST_KIND_LABEL,
  requestKind,
  sessionAllowOption,
  stopRunHint,
  taskStatusMeta,
  taskStripLine,
  splitA2uiSegments,
  toolGroupView,
} from '@aioe/shared';
import { OWNER_REQUIRED_SENTENCE, riskTone, type ToolRiskLevel } from '@aioe/shared/wording/riskWords';
import { ArrowUp, ChevronDown, ChevronRight, ClockArrowRotateLeft, Comments, Cpu, Ellipsis, Eye, Gear, Globe, LayoutList, Pause, Pencil, Play, ShieldExclamation, Square, TerminalLine, TriangleExclamation } from '@gravity-ui/icons';
import { ChatMessage } from '@heroui-pro/react/chat-message';
import { ChatTool, ChatToolGroup } from '@heroui-pro/react/chat-tool';
import { Chip, Dropdown, Label, TextArea, TextField } from '@heroui/react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { PlanWrapUpMessage } from '../plans/PlanWrapUpMessage.tsx';
import { Button, ErrorNote } from '../ui.tsx';
import { FitText } from '../ui/FitText.tsx';
import { Hint } from '../ui/Hint.tsx';
import { approvalRisk, clockWords, type LiveItem, type LiveProgress } from './liveModel.ts';
import { ApprovalDetail, AskCard, LimitBanner, MarkerLine, ReplyChanges, SystemLine, type OpenTurnDiff } from './LiveTurnParts.tsx';
import { A2uiBroken, A2uiCard, type BlockActions, MessageBlocks } from './LiveBlocks.tsx';
import { RichMarkdown } from './RichMarkdown.tsx';
import type { LiveConnection, useLiveSession } from './useLiveSession.ts';

const VIA_WORDS: Record<string, string> = { phone: 'from the phone', web: 'from the web', desktop: 'at the workbench' };
const INTENT_WORDS: Record<string, string> = { steer: 'steers', reply: 'replied', answer: 'answered' };

export function connectionMark(c: LiveConnection): { state: 'disabled' | 'offline' | 'error'; label: string } | undefined {
  if (c === 'connecting') return { state: 'disabled', label: 'Connecting' };
  if (c === 'reconnecting') return { state: 'disabled', label: 'Reconnecting' };
  if (c === 'offline') return { state: 'offline', label: 'Not reachable' };
  if (c === 'refused') return { state: 'error', label: 'Not available' };
  return undefined;
}

// ── Tool calls ──────────────────────────────────────────────────────────

const KIND_ICON = { memory: Cpu, board: LayoutList, agents: Comments, read: Eye, edit: Pencil, shell: TerminalLine, web: Globe, think: Gear, other: Gear } as const;

/**
 * What the agent did, as HeroUI Pro tool calls: one call on its own, or a group whose one-line trigger says what is
 * happening now — or, once over, what was done as a sentence — and opens to the calls. Each call opens to what ran (the
 * call's own words when the stream carried them) and, for one that failed, why. `agent`: the member who made these
 * calls, named above them. `running`: the session is still mid-turn; `tail`: this is the transcript's latest group.
 */
function ToolCalls({ items, agent, running, tail }: { items: LiveToolItem[]; agent?: string; running: boolean; tail: boolean }) {
  const group = toolGroupView(items.map(liveToolActivity), running, tail);
  const shown = items.slice(-MAX_TOOL_CALLS);
  const calls = group.calls.map((c, i) => {
    const Icon = KIND_ICON[c.kind];
    const item = shown[i];
    const ran = item?.detail?.trim() || c.detail;
    const failed = item?.failed?.trim();
    return (
      <ChatTool key={c.key} state={c.state} toolName={c.name} active={c.live} isExpandable={Boolean(ran || failed)} className="text-xs">
        <ChatTool.Trigger>
          <ChatTool.StatusIcon>{c.state === 'output-available' || c.state === 'output-error' ? undefined : <Icon />}</ChatTool.StatusIcon>
          <FitText>{c.name}</FitText>
        </ChatTool.Trigger>
        {ran || failed ? (
          <ChatTool.Content>
            {ran ? (
              <ChatTool.Args label="Call">
                <p className="m-0 whitespace-pre-wrap break-all font-mono text-xs leading-4 text-muted">{ran}</p>
              </ChatTool.Args>
            ) : null}
            {failed ? (
              <p className="m-0 text-xs leading-4 text-danger" data-tool-failed>
                {failed}
              </p>
            ) : null}
          </ChatTool.Content>
        ) : null}
      </ChatTool>
    );
  });
  const body =
    group.calls.length === 1 && !group.hidden ? (
      <>{calls}</>
    ) : (
      <ChatToolGroup active={group.live} className="text-xs">
        <ChatToolGroup.Trigger>{group.failed ? `${group.summary} · ${group.failed} failed` : group.summary}</ChatToolGroup.Trigger>
        <ChatToolGroup.Content>
          {group.hidden > 0 ? <p className="m-0 text-xs text-muted">{group.hidden} earlier call{group.hidden === 1 ? '' : 's'} not shown</p> : null}
          {calls}
        </ChatToolGroup.Content>
      </ChatToolGroup>
    );
  if (!agent) return body;
  return (
    <div className="flex min-w-0 flex-col gap-0.5" data-tool-agent={agent}>
      <span className="text-xs font-medium text-muted">{agent}</span>
      {body}
    </div>
  );
}

// ── The conversation ────────────────────────────────────────────────────

/** What a message row needs from the page around it. */
interface RowContext {
  person: string;
  me?: string;
  projectId?: string;
  canAnswer: boolean;
  answers: Map<string, LiveMessageItem>;
  onAnswer: (questionId: string, text: string) => Promise<string | undefined>;
  /** What a turn's blocks and forms may do: send the person's answer, when they control the session. */
  blocks: BlockActions;
}

/** An agent's words: Markdown with the blocks drawn from it, and any form it embedded (```a2ui), never as raw JSON. */
function AgentWords({ text, actions }: { text: string; actions: BlockActions }) {
  const segments = useMemo(() => splitA2uiSegments(text), [text]);
  if (segments.length === 1 && segments[0]!.kind === 'markdown') return <RichMarkdown text={text} />;
  return (
    <div className="flex min-w-0 flex-col gap-2">
      {segments.map((seg, i) => (seg.kind === 'a2ui' ? <A2uiCard key={i} spec={seg.spec} actions={actions} /> : seg.kind === 'a2ui-broken' ? <A2uiBroken key={i} /> : seg.text.trim() ? <RichMarkdown key={i} text={seg.text} /> : null))}
    </div>
  );
}

function UserRow({ m, ctx }: { m: LiveMessageItem; ctx: RowContext }) {
  const who = authorWords(m, ctx.person, ctx.me);
  const what = m.intent && who !== ctx.person ? INTENT_WORDS[m.intent] : undefined;
  return (
    <ChatMessage.User>
      <ChatMessage.Bubble>
        <ChatMessage.Content>
          <p className="m-0 whitespace-pre-wrap break-words">{m.text}</p>
        </ChatMessage.Content>
      </ChatMessage.Bubble>
      <div className="mt-0.5 flex justify-end text-xs text-muted">{[who, what, m.via ? VIA_WORDS[m.via] : undefined, clockWords(m.at)].filter(Boolean).join(' · ')}</div>
    </ChatMessage.User>
  );
}

function AgentRow({ m, ctx }: { m: LiveMessageItem; ctx: RowContext }) {
  return (
    <div className="flex min-w-0 flex-col gap-1" data-author={m.author?.name}>
      <div className="flex items-baseline gap-2">
        <span className="text-xs font-semibold text-foreground">{authorWords(m, ctx.person, ctx.me)}</span>
        <span className="text-xs text-muted">{clockWords(m.at)}</span>
      </div>
      {m.text.trim() ? (
        <div className="live-reply">
          <AgentWords text={m.text} actions={ctx.blocks} />
        </div>
      ) : null}
      {m.blocks?.length ? <MessageBlocks blocks={m.blocks} actions={ctx.blocks} messageId={m.id} /> : null}
      {m.asks ? <AskCard message={m} me={ctx.me} answer={ctx.answers.get(m.asks.questionId)} canAnswer={ctx.canAnswer} onAnswer={ctx.onAnswer} /> : null}
    </div>
  );
}

function SystemRow({ m, ctx }: { m: LiveMessageItem; ctx: RowContext }) {
  if (m.marker?.kind === 'plan-wrapup') return <PlanWrapUpMessage projectId={ctx.projectId} marker={m.marker} />;
  if (m.marker) return <MarkerLine marker={m.marker} />;
  return m.text.trim() ? <SystemLine text={m.text} /> : null;
}

/** The conversation, newest at the foot; it follows new lines while the reader is at the bottom. `fill`: it takes its frame's height. */
export function Transcript({ items, ready, who, me, projectId, canControl, canAnswer = false, onAnswer, onSend, onPress, conversationId, onOpenDiff, running, fill }: {
  items: LiveItem[];
  ready: boolean;
  who?: string;
  /** The signed-in person's id: their own messages read "You", a question asked of them is theirs to answer. */
  me?: string;
  /** The project the session belongs to, for a plan's Wrap-up card. */
  projectId?: string;
  canControl: boolean;
  /** This reader may answer a question here (controls the session, the machine is reachable). */
  canAnswer?: boolean;
  onAnswer?: (questionId: string, text: string) => Promise<string | undefined>;
  /** Sends the person's answer to a turn's form or app as their next message; only while they may act (as `canAnswer`). */
  onSend?: (text: string) => Promise<string | undefined>;
  /** D414: a press on a generative UI block (`ui.press`), only while they may act (as `canAnswer`); absent, UI blocks are read only. */
  onPress?: BlockActions['press'];
  /** The conversation the transcript is of, which a press names. */
  conversationId?: string;
  /** D414: reads one file of a finished turn that names its checkpoint; absent, a turn's files are listed only. */
  onOpenDiff?: OpenTurnDiff;
  running: boolean;
  fill?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const last = items.at(-1);
  const size = last ? `${items.length}:${last.kind === 'message' ? last.text.length : last.kind === 'tool' ? (last.state ?? '') : last.files.length}` : '';
  useEffect(() => {
    const el = box.current;
    if (el && atBottom.current) el.scrollTop = el.scrollHeight;
  }, [size]);
  const rows = useMemo(() => liveRows(items), [items]);
  const answers = useMemo(() => answersByQuestion(items), [items]);
  const frame = `live-thread overflow-y-auto px-4 py-4 ${fill ? 'min-h-0 flex-1' : 'max-h-[65vh] min-h-48'}`;
  if (!ready) return <div className={frame}><p className="m-0 text-sm text-muted">Reading the session…</p></div>;
  if (!items.length) return <div className={frame}><p className="m-0 text-sm text-muted">Nothing has been said yet. Messages and the agent’s work show here as they happen.</p></div>;
  const ctx: RowContext = { person: canControl ? 'You' : (who ?? 'The person'), me, projectId, canAnswer, answers, onAnswer: onAnswer ?? (async () => 'This session cannot be answered from here.'), blocks: { ...(canAnswer && onSend ? { send: onSend } : {}), ...(canAnswer && onPress ? { press: onPress } : {}), ...(conversationId ? { conversationId } : {}), whose: canControl ? 'your workbench' : who ? `${who}’s workbench` : 'the workbench' } };
  return (
    <div
      ref={box}
      className={frame}
      onScroll={(e) => {
        const el = e.currentTarget;
        atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
      }}
    >
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-4">
        {rows.map((row, i) =>
          row.kind === 'tools' ? (
            <div key={row.key} className="ps-1">
              <ToolCalls items={row.items} agent={row.agent} running={running} tail={i === rows.length - 1} />
            </div>
          ) : row.item.kind === 'changes' ? (
            <ReplyChanges key={row.item.key} item={row.item} onOpenDiff={onOpenDiff} />
          ) : row.item.role === 'user' ? (
            <UserRow key={row.item.key} m={row.item} ctx={ctx} />
          ) : row.item.role === 'system' ? (
            <SystemRow key={row.item.key} m={row.item} ctx={ctx} />
          ) : (
            <AgentRow key={row.item.key} m={row.item} ctx={ctx} />
          ),
        )}
      </div>
    </div>
  );
}

// ── Approvals ───────────────────────────────────────────────────────────

const RISK_TEXT = { muted: 'text-muted', warning: 'text-warning', danger: 'text-danger' } as const;
const levelOf = (n: number): ToolRiskLevel => (Math.min(5, Math.max(1, Math.round(n) || 1)) as ToolRiskLevel);

/**
 * One approval request, as the desktop's card shows it: what kind it is, who asks, `1/2` when several wait, the request
 * in a line, the command it would run or the change it would make, its risk in words (and that an owner must approve a
 * 4 or 5, and that the session has read something from outside), then Approve, "Allow for this session" in a small menu
 * where the runtime offers it, and Decline. A person who is not an owner may still decline. A request its agent can no
 * longer act on says so, and offers Ask again and Dismiss.
 */
export function ApprovalCard({ a, kindOf, position, canAnswer, mayAct, owner, who, resolve }: {
  a: LiveApproval;
  /** The ACP kind of the call it waits on, read from the stream's tool calls: for a workbench from before D372. */
  kindOf?: string;
  position?: string;
  canAnswer: boolean;
  mayAct: boolean;
  owner?: boolean;
  who?: string;
  resolve: (a: LiveApproval, answer: ApprovalAnswer) => Promise<string | undefined>;
}) {
  const [busy, setBusy] = useState<string>();
  const [problem, setProblem] = useState<string>();
  const live = isLive(a.answerable);
  const level = levelOf(a.riskLevel);
  const ownerOnly = a.ownerRequired || level >= 4;
  const mayApprove = mayAct && !(ownerOnly && owner === false);
  const session = sessionAllowOption(a.options);
  const kind = REQUEST_KIND_LABEL[requestKind(a.requestKind, a.toolKind ?? kindOf)];
  const title = a.title?.trim() || 'Use a tool';
  const answer = async (key: string, ans: ApprovalAnswer) => {
    setBusy(key);
    setProblem(undefined);
    setProblem(await resolve(a, ans));
    setBusy(undefined);
  };
  return (
    <div className={`flex flex-col gap-1.5 rounded-xl border px-3 py-2.5 ${live ? 'border-warning/35 bg-warning/5' : 'border-separator bg-surface-secondary'}`} role="group" aria-label={`${kind}: ${title}`} data-permission={a.permissionId} data-answerable={a.answerable ?? 'live'}>
      <div className="flex min-w-0 items-center gap-1.5 text-xs">
        {live ? <ShieldExclamation className="size-3.5 shrink-0 text-warning" aria-hidden /> : <ClockArrowRotateLeft className="size-3.5 shrink-0 text-muted" aria-hidden />}
        <span className="font-medium text-foreground">{kind}</span>
        {a.agentName ? <FitText className="min-w-0 text-muted">{a.agentName}</FitText> : null}
        {position ? (
          <span className="ms-auto shrink-0 tabular-nums text-muted" aria-label={`request ${position.replace('/', ' of ')}`}>
            {position}
          </span>
        ) : null}
      </div>
      <span className="min-w-0 break-words text-sm text-foreground">{title}</span>
      {a.detail ? <ApprovalDetail detail={a.detail} /> : null}
      {live ? (
        <span className="flex min-w-0 flex-col text-xs leading-4" data-risk-level={level}>
          <span className={`break-words ${RISK_TEXT[riskTone(level)]}`}>{approvalRisk(a)}</span>
          {ownerOnly ? <span className="font-medium text-foreground">{OWNER_REQUIRED_SENTENCE}{owner === false ? ' You can decline it.' : ''}</span> : null}
          {a.outside?.trim() ? <span className="break-words text-muted" data-outside>{a.outside}</span> : null}
        </span>
      ) : (
        <span className="text-xs leading-4 text-muted">The agent can no longer act on an answer.</span>
      )}
      {!canAnswer ? (
        <p className="m-0 text-xs text-muted">Only {who ?? 'the person it runs for'} can answer this.</p>
      ) : live ? (
        <div className="flex flex-wrap items-center gap-1 pt-0.5">
          <Button size="sm" variant="primary" isDisabled={!mayApprove || Boolean(busy)} isPending={busy === 'approve'} onPress={() => void answer('approve', ANSWERS.approve)}>
            Approve
          </Button>
          {session ? (
            <Dropdown>
              <Hint text="More ways to answer">
                <Button size="sm" variant="ghost" isIconOnly aria-label="More ways to answer" isDisabled={!mayApprove || Boolean(busy)}>
                  <ChevronDown className="size-3.5" />
                </Button>
              </Hint>
              <Dropdown.Popover placement="bottom start" className="max-w-80">
                <Dropdown.Menu aria-label="More ways to answer" onAction={(k) => (k === 'session' ? void answer('session', ANSWERS.session(session)) : undefined)}>
                  <Dropdown.Item id="session" textValue="Allow for this session">
                    <Label>Allow for this session</Label>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown>
          ) : null}
          <Button size="sm" variant="danger-soft" isDisabled={!mayAct || Boolean(busy)} isPending={busy === 'decline'} onPress={() => void answer('decline', ANSWERS.decline)}>
            Decline
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-1 pt-0.5">
          <Button size="sm" variant="secondary" isDisabled={!mayAct || Boolean(busy)} isPending={busy === 'again'} onPress={() => void answer('again', ANSWERS.askAgain)}>
            Ask again
          </Button>
          <Button size="sm" variant="ghost" isDisabled={!mayAct || Boolean(busy)} isPending={busy === 'dismiss'} onPress={() => void answer('dismiss', ANSWERS.dismiss)}>
            Dismiss
          </Button>
        </div>
      )}
      <ErrorNote error={problem} />
    </div>
  );
}

// ── The task strip ──────────────────────────────────────────────────────

const CHIP: Record<string, 'default' | 'accent' | 'success' | 'warning' | 'danger'> = { default: 'default', accent: 'accent', success: 'success', warning: 'warning', danger: 'danger' };

/**
 * The tasks this conversation put on the board, with where each stands, who the run waits for, and — where this reader
 * may (`onPause`) — Pause all / Resume all; it starts folded past a handful.
 */
export function TaskStrip({ tasks, paused, onPause }: { tasks: LiveProgress[]; paused?: boolean; onPause?: (paused: boolean) => Promise<string | undefined> }) {
  const [open, setOpen] = useState(tasks.length <= 3);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();
  if (!tasks.length) return null;
  const active = tasks.filter((t) => t.state !== 'done').length;
  const waitingFor = personWaitLine(tasks.map((t) => ({ id: t.taskId, status: t.state ?? '', ...(t.assignee ? { assignee: t.assignee } : {}), ...(t.forPerson ? { forPerson: true } : {}) })));
  const toggle = async () => {
    if (!onPause) return;
    setBusy(true);
    setProblem(undefined);
    setProblem(await onPause(!paused));
    setBusy(false);
  };
  return (
    <div className="border-b border-separator px-4 py-2" aria-label="Tasks from this conversation" data-task-strip>
      <div className="flex min-w-0 items-center gap-2">
        <button type="button" className="flex min-w-0 flex-1 cursor-[var(--cursor-interactive)] items-center gap-2 bg-transparent text-left" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          <span className="text-muted">{open ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}</span>
          <span className="shrink-0 text-xs font-medium text-foreground">Tasks from this chat</span>
          <FitText className="min-w-0 text-xs tabular-nums text-muted">{taskStripLine(tasks.map((t) => ({ status: t.state, ...(t.needsHuman ? { needsHuman: true } : {}) })))}</FitText>
          {paused ? (
            <Chip size="sm" variant="soft" color="warning" className="shrink-0">
              <Pause className="inline size-3" /> paused
            </Chip>
          ) : null}
        </button>
        {onPause && (active > 0 || paused) ? (
          <Hint text={paused ? 'Resume the board: the parked tasks are sent again' : 'Pause the board: work under way finishes, nothing new starts'}>
            <Button size="sm" variant={paused ? 'primary' : 'tertiary'} className="shrink-0" isPending={busy} onPress={() => void toggle()} aria-label={paused ? 'Resume the board, sending the parked tasks again' : 'Pause the board: work under way finishes, nothing new starts'}>
              {paused ? <Play className="size-3" /> : <Pause className="size-3" />}
              {paused ? 'Resume all' : 'Pause all'}
            </Button>
          </Hint>
        ) : null}
      </div>
      {waitingFor ? (
        <p className="m-0 mt-0.5 ps-5 text-xs text-muted" title="A person’s task waits for them; the run stays open, idle, until they act">
          {waitingFor}
        </p>
      ) : null}
      {paused ? <p className="m-0 mt-1 ps-5 text-xs text-muted">Paused: whatever an agent was mid-turn on finishes, but no new work is sent. Resume sends the parked tasks again.</p> : null}
      <ErrorNote error={problem} />
      {open ? (
        <ul className="m-0 mt-1.5 flex max-h-36 list-none flex-col gap-1 overflow-y-auto p-0">
          {tasks.map((t) => {
            const meta = taskStatusMeta(t.state);
            return (
              <li key={t.taskId} className="flex min-w-0 items-center gap-2 px-1 py-0.5">
                <span className="shrink-0 text-xs tabular-nums text-muted">{t.taskId}</span>
                <FitText className="min-w-0 flex-1 text-xs text-foreground">{t.title?.trim() || 'A task'}</FitText>
                {t.assignee ? <FitText className="hidden max-w-32 shrink-0 text-xs text-muted sm:block">{t.assignee}</FitText> : null}
                {t.needsHuman ? (
                  <Hint text="Needs a person’s review">
                    <span className="shrink-0 text-warning" aria-label="Needs a person’s review">
                      <TriangleExclamation className="size-3" />
                    </span>
                  </Hint>
                ) : null}
                <Chip size="sm" variant="soft" color={CHIP[meta.color]} className="shrink-0">
                  {meta.label}
                </Chip>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

// ── What docks above the composer ───────────────────────────────────────

/** One line above the composer, as the desktop's banner stack shows it: what is wrong or who else holds the session. */
export function LiveBanner({ tone, title, detail }: { tone: 'warning' | 'notice'; title: string; detail?: string }) {
  return (
    <div className={`rounded-xl border px-3 py-2 text-xs ${tone === 'warning' ? 'border-warning/35 bg-warning/5' : 'border-separator bg-surface/60'}`} role="status">
      <p className="m-0 font-medium text-foreground">{title}</p>
      {detail ? <p className="m-0 text-muted">{detail}</p> : null}
    </div>
  );
}

type Live = ReturnType<typeof useLiveSession>;

/**
 * The composer: the person's next message and the one button the moment calls for — Send, or Stop while the agent is
 * working and nothing is typed (with a small Stop beside Send when something is). Stop is the run's, never the session's.
 */
function Composer({ live, mayAct, locked, running, mode, placeholder, onProblem }: { live: Live; mayAct: boolean; /** An approval is open: answer it first (Stop still works). */ locked: boolean; running: boolean; mode?: RemoteSessionMode; placeholder: string; onProblem: (p: string | undefined) => void }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState<'send' | 'stop'>();
  const run = async (what: 'send' | 'stop', fn: () => Promise<string | undefined>) => {
    setBusy(what);
    onProblem(undefined);
    const p = await fn();
    setBusy(undefined);
    onProblem(p);
    return !p;
  };
  const send = async () => {
    const t = text.trim();
    if (!t) return;
    if (await run('send', () => live.send(t))) setText('');
  };
  const stop = () => void run('stop', live.interrupt);
  const hint = stopRunHint(mode);
  const hasText = Boolean(text.trim());
  return (
    <div className="flex items-end gap-2">
      <TextField className="min-w-0 flex-1" variant="secondary" value={text} onChange={setText} isDisabled={!mayAct || locked}>
        <Label className="sr-only">Your next message</Label>
        <TextArea
          className="w-full resize-none"
          rows={2}
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              if (mayAct && !locked && !busy) void send();
            }
          }}
        />
      </TextField>
      {running && hasText ? (
        <Hint text={hint}>
          <Button size="sm" variant="ghost" isIconOnly aria-label={hint} isDisabled={!mayAct || Boolean(busy)} isPending={busy === 'stop'} onPress={stop}>
            <Square className="size-3.5" />
          </Button>
        </Hint>
      ) : null}
      {running && !hasText ? (
        <Hint text={hint}>
          <Button size="sm" variant="secondary" aria-label={`Stop: ${hint}`} isDisabled={!mayAct || Boolean(busy)} isPending={busy === 'stop'} onPress={stop}>
            <Square className="size-3.5" />
            Stop
          </Button>
        </Hint>
      ) : (
        <Button size="sm" variant="primary" isDisabled={!mayAct || locked || !hasText || Boolean(busy)} isPending={busy === 'send'} onPress={() => void send()}>
          <ArrowUp className="size-3.5" />
          Send
        </Button>
      )}
    </div>
  );
}

/**
 * Everything below a live session's header: the task strip, the conversation, and docked at its foot the banners (the
 * usage limit among them), the first approval waiting ("1/2" when more do) and the composer — or, for someone
 * watching, a line saying so.
 */
export function LiveConversation({ live, canControl, who, me, owner, mode, projectId, canPause, runtimes, banners, fill }: {
  live: Live;
  canControl: boolean;
  /** The person it runs for, by first name, when known. */
  who?: string;
  /** The signed-in person's id. */
  me?: string;
  /** Whether the person here is an owner of the project (levels 4 and 5), when known. */
  owner?: boolean;
  mode?: RemoteSessionMode;
  /** The project the session belongs to, when known: a plan's Wrap-up card reads it. */
  projectId?: string;
  /** The machine lets this console change its task board (`tasks:write`): Pause all / Resume all is offered. */
  canPause?: boolean;
  /** The runtimes the machine last reported (its facts, with their named accounts since D414): where "Hand off to…" may send the session at its usage limit. */
  runtimes?: readonly ReportedRuntime[];
  /** Lines above the composer the page knows about (who else holds it, the machine is away). */
  banners?: ReactNode;
  fill?: boolean;
}) {
  const { state, connection } = live;
  const [problem, setProblem] = useState<string>();
  const reachable = connection === 'live';
  const remoteOff = state.remote?.on === false;
  const mayAct = canControl && reachable && !remoteOff;
  const running = state.status === 'thinking' || state.status === 'waiting';
  const first = state.approvals[0];
  // Before D372 an approval did not name its call's kind: read it from the newest call of the same title.
  const kindOf = first && !first.requestKind && !first.toolKind ? approvalToolKind(first.title, state.items.filter((x): x is LiveToolItem => x.kind === 'tool')) : undefined;
  // At its usage limit the stop is the limit's: its words are the banner's "What the backend said", not a banner of their own.
  const stopped = state.status === 'stopped' && state.message && !state.limit ? state.message : undefined;
  const limitSession = state.limit?.sessionId;
  // D414: the same runtime's other signed-in accounts first (never the one at its limit), then the other runtimes.
  const handOffOptions = useMemo(() => remoteHandOffChoices(state.limit?.provider, runtimes, state.limit?.account), [state.limit?.provider, state.limit?.account, runtimes]);
  const placeholder = remoteOff ? 'Remote control is off for this session' : !reachable ? 'Waiting for the machine' : running ? 'Send a message; it goes when the agent is ready' : 'Your next message';
  // Pausing the board needs the machine's task-board scope, the board named, and this session open to remote control.
  const teamId = state.teamId;
  const onPause = canPause && mayAct && teamId && state.remote?.on ? (paused: boolean) => live.setPaused(teamId, paused) : undefined;
  return (
    <div className={`flex min-h-0 flex-col ${fill ? 'flex-1' : ''}`}>
      {connection === 'refused' && live.problem ? <p className="m-0 px-4 pt-3 text-sm text-muted">{live.problem}</p> : null}
      <TaskStrip tasks={state.progress} paused={state.paused} onPause={onPause} />
      <Transcript
        items={state.items}
        ready={state.ready || connection === 'refused' || connection === 'offline'}
        who={who}
        me={me}
        projectId={projectId}
        canControl={canControl}
        canAnswer={mayAct}
        onAnswer={live.answer}
        onSend={live.send}
        onPress={live.canPress ? live.press : undefined}
        conversationId={live.conversationId}
        onOpenDiff={canControl && reachable ? live.readDiff : undefined}
        running={running}
        fill={fill}
      />
      <div className="flex flex-col gap-2 border-t border-separator px-4 py-3">
        {stopped ? <LiveBanner tone="warning" title="The session stopped" detail={stopped} /> : null}
        {state.limit ? <LimitBanner limit={state.limit} options={handOffOptions} onHandOff={mayAct && limitSession ? (o) => live.handOff(limitSession, o.target) : undefined} /> : null}
        {banners}
        {first ? (
          <div role="region" aria-label="Waiting for your approval">
            <ApprovalCard
              key={first.permissionId}
              a={first}
              kindOf={kindOf}
              position={state.approvals.length > 1 ? `1/${state.approvals.length}` : positionLabel({ id: first.permissionId, sessionId: first.sessionId }, state.approvals.map((x) => ({ id: x.permissionId, sessionId: x.sessionId })))}
              canAnswer={canControl}
              mayAct={mayAct}
              owner={owner}
              who={who}
              resolve={live.resolve}
            />
          </div>
        ) : null}
        <ErrorNote error={problem} />
        {canControl ? (
          <Composer live={live} mayAct={mayAct} locked={Boolean(first)} running={running} mode={mode} placeholder={first ? 'Answer the approval above first' : placeholder} onProblem={setProblem} />
        ) : (
          <p className="m-0 text-sm text-muted">You are watching; only {who ?? 'the person it runs for'} can send.</p>
        )}
      </div>
    </div>
  );
}

// ── The session's menu ──────────────────────────────────────────────────

/**
 * The header's one control for the person it runs for: a menu with Stop remote control (the desktop's words: the session
 * goes back to the desktop alone) and, for an agent session of its own, End the agent session. Stopping the run is the
 * composer's Stop; a conversation (Chat, Research, Team Work) has no session to end from here.
 */
export function SessionMenu({ remoteOn, reachable, agentSession, onStopRemote, onEnd }: { remoteOn: boolean; reachable: boolean; agentSession: boolean; onStopRemote: () => void; onEnd: () => void }) {
  if (!remoteOn && !agentSession) return null;
  return (
    <Dropdown>
      <Hint text="More for this session">
        <Button size="sm" variant="ghost" isIconOnly aria-label="More for this session">
          <Ellipsis className="size-4" />
        </Button>
      </Hint>
      <Dropdown.Popover placement="bottom end" className="w-80 max-w-[calc(100vw-2rem)]">
        <Dropdown.Menu aria-label="More for this session" disabledKeys={reachable ? [] : ['remote', 'end']} onAction={(k) => (k === 'remote' ? onStopRemote() : k === 'end' ? onEnd() : undefined)}>
          {remoteOn ? (
            <Dropdown.Item id="remote" textValue="Stop remote control">
              <div className="flex flex-col">
                <Label>Stop remote control</Label>
                <span className="text-xs text-muted">Hands it back to the desktop. Nobody can send from the web or the phone until it is turned on there again.</span>
              </div>
            </Dropdown.Item>
          ) : null}
          {agentSession ? (
            <Dropdown.Item id="end" textValue="End the agent session" variant="danger">
              <div className="flex flex-col">
                <Label>End the agent session…</Label>
                <span className="text-xs text-muted">The agent stops on the machine. What it has done so far stays.</span>
              </div>
            </Dropdown.Item>
          ) : null}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown>
  );
}
